import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

export interface ProjectStageSchedule {
  id: string;
  stage_name: string;
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
  status: string;
}

export interface UseDailyLogStageScheduleResult {
  stageSchedules: Record<string, ProjectStageSchedule[]>;
  loading: boolean;
  error: string | null;
  getMatchingStagesForDate: (dateKey: string, projectId: string) => ProjectStageSchedule[];
  getStageForDate: (dateKey: string, projectId: string) => ProjectStageSchedule | null;
}

function isItemComplete(item: {
  is_complete: boolean;
  progress: number;
  actual_end_date: string | null;
}): boolean {
  if (item.is_complete) return true;
  if (item.progress >= 100) return true;
  if (item.actual_end_date != null && item.actual_end_date !== '') return true;
  return false;
}

/** Validate a string is a proper UUID. */
function isValidUUID(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

export function useDailyLogStageSchedule(projectIds: string[]): UseDailyLogStageScheduleResult {
  const [stageSchedules, setStageSchedules] = useState<Record<string, ProjectStageSchedule[]>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (projectIds.length === 0) {
      setStageSchedules({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // ── 1. Load REAL project stages (source of truth) ──
      const { data: realStageRows, error: realErr } = await supabase
        .from('jphph_project_stages')
        .select('id, project_id, stage_name, sort_order, start_date, end_date, status')
        .in('project_id', projectIds);

      if (realErr) throw realErr;

      // Build map: projectId -> real stages
      const realByProject = new Map<string, typeof realStageRows>();
      for (const row of realStageRows || []) {
        const arr = realByProject.get(row.project_id) || [];
        arr.push(row);
        realByProject.set(row.project_id, arr);
      }

      const byProject: Record<string, ProjectStageSchedule[]> = {};

      for (const projectId of projectIds) {
        const realStages = realByProject.get(projectId);

        if (realStages && realStages.length > 0) {
          // Use REAL project stages directly
          const mapped: ProjectStageSchedule[] = realStages
            .map((s) => ({
              id: s.id,
              stage_name: s.stage_name,
              start_date: s.start_date,
              end_date: s.end_date,
              sort_order: s.sort_order ?? 0,
              status: s.status || 'not_started',
            }))
            .sort((a, b) => a.sort_order - b.sort_order);

          byProject[projectId] = mapped;
          continue;
        }

        // ── 2. Fallback: derive from schedule items when no project_stages exist ──
        const { data: scheduleData, error: scheduleErr } = await supabase
          .from('jphph_project_schedule_items')
          .select('id, project_id, title, main_stage, phase, start_date, end_date, actual_start_date, actual_end_date, progress, is_complete, is_included, sort_order')
          .eq('project_id', projectId)
          .eq('is_included', true)
          .eq('is_deleted', false)
          .order('sort_order', { ascending: true });

        if (scheduleErr) throw scheduleErr;

        const items = scheduleData || [];
        const groups = new Map<string, typeof items>();

        for (const item of items) {
          const stageName = item.main_stage || item.phase || 'General';
          if (!groups.has(stageName)) groups.set(stageName, []);
          groups.get(stageName)!.push(item);
        }

        // Build a lookup from stageName -> real stage UUID (for this project)
        const stageUuidMap = new Map<string, string>();
        for (const row of realStageRows || []) {
          if (row.project_id !== projectId) continue;
          const key = row.stage_name;
          if (!stageUuidMap.has(key)) {
            stageUuidMap.set(key, row.id);
          }
        }

        const derived: ProjectStageSchedule[] = [];
        for (const [stageName, stageItems] of groups) {
          const total = stageItems.length;
          const completed = stageItems.filter(isItemComplete).length;
          const completionPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

          let status = 'not_started';
          if (completionPercent >= 100) status = 'completed';
          else if (completionPercent > 0) status = 'in_progress';

          const startDates = stageItems.map((i) => i.start_date).filter((d): d is string => !!d);
          const endDates = stageItems.map((i) => i.end_date).filter((d): d is string => !!d);
          const actualStartDates = stageItems.map((i) => i.actual_start_date).filter((d): d is string => !!d);
          const actualEndDates = stageItems.map((i) => i.actual_end_date).filter((d): d is string => !!d);

          const startDate = startDates.length > 0 ? startDates.sort()[0] : null;
          const endDate = endDates.length > 0 ? endDates.sort((a, b) => a.localeCompare(b))[endDates.length - 1] : null;
          const actualStart = actualStartDates.length > 0 ? actualStartDates.sort()[0] : null;
          const actualEnd = actualEndDates.length > 0 ? actualEndDates.sort((a, b) => a.localeCompare(b))[actualEndDates.length - 1] : null;

          const minSort = Math.min(...stageItems.map((i) => i.sort_order));

          const realStageId = stageUuidMap.get(stageName);
          const stageId = realStageId && isValidUUID(realStageId) ? realStageId : `${projectId}::${stageName}`;

          derived.push({
            id: stageId,
            stage_name: stageName,
            start_date: actualStart || startDate,
            end_date: actualEnd || endDate,
            sort_order: minSort,
            status,
          });
        }

        // Sort by actual date order
        derived.sort((a, b) => {
          const aStart = a.start_date || '9999-12-31';
          const bStart = b.start_date || '9999-12-31';
          if (aStart !== bStart) return aStart.localeCompare(bStart);
          return a.sort_order - b.sort_order;
        });

        byProject[projectId] = derived;
      }

      setStageSchedules(byProject);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [projectIds.join(',')]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: refresh when schedule items or project stages change
  useEffect(() => {
    if (projectIds.length === 0) return;
    const channel = supabase
      .channel(`daily-log-stages-${projectIds.join('-')}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_project_schedule_items',
          filter: `project_id=in.(${projectIds.join(',')})`,
        },
        () => {
          setTimeout(() => load(), 300);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_project_stages',
          filter: `project_id=in.(${projectIds.join(',')})`,
        },
        () => {
          setTimeout(() => load(), 300);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [projectIds.join(','), load]);

  const getMatchingStagesForDate = useCallback(
    (dateKey: string, projectId: string): ProjectStageSchedule[] => {
      const stages = stageSchedules?.[projectId] ?? [];
      return stages.filter((s) => {
        if (!s.start_date || !s.end_date) return false;
        return dateKey >= s.start_date && dateKey <= s.end_date;
      });
    },
    [stageSchedules]
  );

  const getStageForDate = useCallback(
    (dateKey: string, projectId: string): ProjectStageSchedule | null => {
      const matching = getMatchingStagesForDate(dateKey, projectId);
      if (matching.length === 0) return null;
      if (matching.length === 1) return matching[0];
      const inProgress = matching.find((s) => s.status === 'in_progress');
      if (inProgress) return inProgress;
      return matching.sort((a, b) => a.sort_order - b.sort_order)[0];
    },
    [getMatchingStagesForDate]
  );

  return { stageSchedules: stageSchedules || {}, loading, error, getMatchingStagesForDate, getStageForDate, refresh: load };
}